import { Scope } from "@sentry/react";

import getSentry, { fingerprintTypes } from "seneca-common/features/Sentry";
import propsToJS from "seneca-common/utils/functions/immutable/props-to-js";

import featureToggles from "features/dev-tools/featureToggles";

const logToConsole =
  import.meta.env.DEV ||
  featureToggles.logSentryToConsole ||
  import.meta.env.VITE_SENTRY_DEV_ACTIVE === "true";

const logToSentry =
  import.meta.env.PROD || import.meta.env.VITE_SENTRY_DEV_ACTIVE === "true";

// send errors to Sentry in production or log to console in dev

type AdditionalInfo = {
  message?: string;
  extraInfo?: { [key: string]: any };
  fingerprint?: string[];
  fingerprintType?: string;
  errorMessage?: string;
  correlationId?: string;
  priority?: "high";
};

function logError(error?: any, context: AdditionalInfo = {}) {
  const Sentry = getSentry();

  const sentryContext = {
    ...context,
    fingerprintType: context.fingerprintType || fingerprintTypes.default,
    correlationId: context.correlationId || error?.correlationId,
    extraInfo: parseExtraInfo(context.extraInfo)
  };

  if (logToConsole) {
    console.error(error);
    console.log("Meta to send to Sentry:", sentryContext);
  }

  if (logToSentry) {
    Sentry.withScope(scope => {
      configureScopeWithAdditionalInfo(scope, sentryContext);
      Sentry.captureException(error);
    });
  }
}

function logMessage(message: string, context: AdditionalInfo = {}) {
  const Sentry = getSentry();

  const sentryContext = {
    ...context,
    fingerprintType: context.fingerprintType || fingerprintTypes.default,
    extraInfo: parseExtraInfo(context.extraInfo)
  };

  if (logToConsole) {
    console.log(message);
    console.log("Meta to send to Sentry: ", sentryContext);
  }

  if (logToSentry) {
    Sentry.withScope(scope => {
      configureScopeWithAdditionalInfo(scope, sentryContext);
      Sentry.captureMessage(message, "info");
    });
  }
}

function parseExtraInfo(extraInfo?: { [key: string]: any }) {
  const extraInfoJS = extraInfo && propsToJS(extraInfo);
  return extraInfoJS;
}

function configureScopeWithAdditionalInfo(
  scope: Scope,
  {
    message,
    errorMessage,
    extraInfo,
    fingerprint,
    fingerprintType,
    correlationId,
    priority
  }: AdditionalInfo
) {
  extraInfo && scope.setExtras(extraInfo);
  // if capturing a message sentry will override the message tag
  // this allows us to logMessage and then get finer grain details
  // in their breakdowns
  errorMessage && scope.setTag("errorMessage", errorMessage);
  message && scope.setTag("message", message);
  correlationId && scope.setTag("correlationId", correlationId);
  priority && scope.setTag("priority", priority);

  if (fingerprint) {
    const fingerprintToSend = fingerprintType
      ? [fingerprintType, ...fingerprint]
      : fingerprint;
    scope.setFingerprint(fingerprintToSend);
  }
}

export { logMessage };
export default logError;
